'use client';

import { memo, useMemo } from 'react';

import { type Board, type BoardsListOptions, type Library } from '@air/api/types';

import { BoardsTreeItem } from '~/components/tree/BoardsTreeItem';
import { type TreeItemProps } from '~/components/tree/TreeItem';
import { TreeLoadMoreRow } from '~/components/tree/TreeLoadMoreRow';
import { useBoardsList } from '~/hooks/api/useBoardsList';
import { useTreePagination } from '~/hooks/useTreePagination';
import { formatBoardsListResponse } from '~/utils/formatBoardsListResponse';

export type SubBoardsTreeProps = Pick<TreeItemProps, 'depth'> & {
  boardId?: Board['id'];
  libraryId?: Library['id'];
  /** Filter for library boards: 'NO' for General Library (boards not in any library), 'ALL' for all boards */
  libraryBoards?: BoardsListOptions['libraryBoards'];
  workspaceId: string;
  onClick: (board: Board) => void;
  onMouseEnter?: (board: Board) => void;
  isDisabled?: (board: Board) => boolean;
  selectedBoardId?: string;
};

export const SubBoardsTree = memo(
  ({
    boardId,
    depth,
    libraryBoards,
    libraryId,
    workspaceId,
    onClick,
    onMouseEnter,
    isDisabled,
    selectedBoardId,
  }: SubBoardsTreeProps) => {
    const boardsQuery = useBoardsList({
      libraryBoards,
      libraryIds: libraryId ? [libraryId] : undefined,
      view: boardId ? boardId : 'root',
      workspaceId,
    });
    const pagination = useTreePagination(boardsQuery);

    const { boards } = useMemo(() => formatBoardsListResponse(boardsQuery.data), [boardsQuery.data]);

    if (!boards || boards.length === 0) {
      return null;
    }

    return (
      <>
        {boards.map((board) => (
          <BoardsTreeItem
            board={board}
            depth={depth + 1}
            isDisabled={isDisabled}
            key={board.id}
            onClick={onClick}
            onMouseEnter={onMouseEnter}
            selectedBoardId={selectedBoardId}
            workspaceId={workspaceId}
          />
        ))}
        <TreeLoadMoreRow depth={depth + 1} pagination={pagination} />
      </>
    );
  },
);

SubBoardsTree.displayName = 'SubBoardsTree';
